import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Assessment as AssessmentIcon,
  DataUsage as DataUsageIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import realDataAccountingService from '../../services/realDataAccountingService';
import autoJournalService from '../../services/autoJournalService';
import accountingDataValidator from '../../services/accountingDataValidator';

const AccountingIntegration = () => {
  const dispatch = useDispatch();
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadIntegrationStatus();
    }
  }, [currentSchool]);

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const loadIntegrationStatus = async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Validate data quality
      const dataQualityReport = accountingDataValidator.generateDataQualityReport(reduxData);
      setDataQuality(dataQualityReport);

      // Get real-time financial summary
      const financialSummary = await autoJournalService.getRealTimeFinancialSummary(
        currentSchool?.schoolID,
        reduxData
      );

      setIntegrationStatus({
        dataQuality: dataQualityReport,
        financialSummary,
        lastChecked: new Date(),
        isConnected: true
      });
    } catch (err) {
      setError('Failed to load integration status');
      console.error('Integration status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncData = async () => {
    if (!currentSchool?.schoolID) return;
    
    setSyncDialogOpen(true);
    setSyncProgress(0);
    
    try {
      setSyncProgress(20);

      // Fix common data issues
      const fixedData = accountingDataValidator.fixCommonDataIssues(reduxData);
      setSyncProgress(40);

      // Sync with accounting system
      const syncResult = await autoJournalService.syncRealDataWithAccounting(
        currentSchool?.schoolID,
        fixedData
      );
      setSyncProgress(80);

      // Refresh integration status
      await loadIntegrationStatus();
      setSyncProgress(100);

      setTimeout(() => {
        setSyncDialogOpen(false);
        setSyncProgress(0);
      }, 1000);

    } catch (err) {
      setError('Failed to sync data');
      console.error('Sync error:', err);
      setSyncDialogOpen(false);
    }
  };

  const getQualityColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getQualityLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading integration status...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadIntegrationStatus}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Accounting System Integration
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Data Quality Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Data Quality</Typography>
              </Box>
              
              {dataQuality && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" sx={{ mr: 2 }}>
                      {dataQuality.overallScore}
                    </Typography>
                    <Chip 
                      label={getQualityLabel(dataQuality.overallScore)}
                      color={getQualityColor(dataQuality.overallScore)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {dataQuality.summary.totalErrors} errors, {dataQuality.summary.totalWarnings} warnings
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={dataQuality.overallScore} 
                    sx={{ mb: 2 }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Summary Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Financial Summary</Typography>
              </Box>
              
              {integrationStatus?.financialSummary && (
                <>
                  <Typography variant="h4" color="primary" sx={{ mb: 1 }}>
                    {integrationStatus.financialSummary.totalRevenue?.toLocaleString() || '0'} GHC
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Expenses: {integrationStatus.financialSummary.totalExpenses?.toLocaleString() || '0'} GHC
                    </Typography>
                    <Typography variant="body2">
                      Net Income: {integrationStatus.financialSummary.netIncome?.toLocaleString() || '0'} GHC
                    </Typography>
                    <Typography variant="body2">
                      Cash Balance: {integrationStatus.financialSummary.cashBalance?.toLocaleString() || '0'} GHC
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Quality Details */}
      {dataQuality && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <DataUsageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Data Quality Details
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data Type</TableCell>
                    <TableCell align="center">Errors</TableCell>
                    <TableCell align="center">Warnings</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Student Finance</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.students.errors.length} 
                        color={dataQuality.students.errors.length > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.students.warnings.length} 
                        color={dataQuality.students.warnings.length > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dataQuality.students.isValid ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell>Expenses</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.expenses.errors.length} 
                        color={dataQuality.expenses.errors.length > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.expenses.warnings.length} 
                        color={dataQuality.expenses.warnings.length > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dataQuality.expenses.isValid ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell>Sales</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.sales.errors.length} 
                        color={dataQuality.sales.errors.length > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.sales.warnings.length} 
                        color={dataQuality.sales.warnings.length > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dataQuality.sales.isValid ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell>Assets</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.assets.errors.length} 
                        color={dataQuality.assets.errors.length > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.assets.warnings.length} 
                        color={dataQuality.assets.warnings.length > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dataQuality.assets.isValid ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell>Teachers</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.teachers.errors.length} 
                        color={dataQuality.teachers.errors.length > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={dataQuality.teachers.warnings.length} 
                        color={dataQuality.teachers.warnings.length > 0 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {dataQuality.teachers.isValid ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {dataQuality?.recommendations && dataQuality.recommendations.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Recommendations
            </Typography>
            
            <List>
              {dataQuality.recommendations.map((rec, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      {rec.priority === 'High' ? (
                        <ErrorIcon color="error" />
                      ) : rec.priority === 'Medium' ? (
                        <WarningIcon color="warning" />
                      ) : (
                        <CheckCircleIcon color="info" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={rec.message}
                      secondary={`${rec.category} • ${rec.count} issues • Priority: ${rec.priority}`}
                    />
                  </ListItem>
                  {index < dataQuality.recommendations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleSyncData}
          disabled={loading}
        >
          Sync Data with Accounting
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadIntegrationStatus}
          disabled={loading}
        >
          Refresh Status
        </Button>
      </Box>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>Syncing Data with Accounting System</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={syncProgress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {syncProgress < 20 && 'Preparing data...'}
              {syncProgress >= 20 && syncProgress < 40 && 'Fixing data issues...'}
              {syncProgress >= 40 && syncProgress < 80 && 'Syncing with accounting system...'}
              {syncProgress >= 80 && syncProgress < 100 && 'Finalizing...'}
              {syncProgress >= 100 && 'Sync completed successfully!'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSyncDialogOpen(false)} 
            disabled={syncProgress < 100}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingIntegration;
